// PAINT SOMETHING TO MOVE (owner 2026-10-23, the 7-step path: "Paint something to move" for water, flags, smoke): the
// customer paints over the picture with a finger; the strokes are kept in 0..1 of the ORIGINAL picture (every shape
// places the picture its own way) and sent as the `paint` field, which the server reads with living-motion.js paintOf
// and draws per shape with the same placement (placementOf here mirrors it).

// More of what moves (owner 2026-10-24: "you should add clouds and other things that move"): clouds, fog, steam, a
// river, car lights (living-motion.js PAINT, flow.js).
export const PAINT_KINDS = ['water', 'waterfall', 'river', 'clouds', 'fog', 'smoke', 'steam', 'traffic', 'flag', 'grass', 'swing'] as const;
export type PaintKind = (typeof PAINT_KINDS)[number];
/** Which way each kind moves at first (living-motion.js PAINT): [dx, dy], y down; a flag's pole side, grass's roots. */
export const PAINT_DIR: Record<PaintKind, [number, number] | null> = {
  water: [0, 1], waterfall: [0, 1], river: [1, 0.15], clouds: [1, 0], fog: [1, 0], smoke: [0.15, -1], steam: [0, -1], traffic: [1, 0], flag: null, grass: null, swing: null,
};
// Swing (owner 2026-10-24: "make the legs seem like they are swinging"): what hangs swings from its top.
export const PAINT_ROOT: Partial<Record<PaintKind, 'left' | 'right' | 'bottom' | 'top'>> = { flag: 'left', grass: 'bottom', swing: 'top' };
/** The painted area's tint on the picture while painting. */
export const PAINT_TINT: Record<PaintKind, string> = {
  water: '56,189,248', waterfall: '125,211,252', river: '45,212,191', clouds: '226,232,240', fog: '148,163,184', smoke: '203,213,225', steam: '241,245,249',
  traffic: '251,146,60', flag: '248,113,113', grass: '74,222,128', swing: '250,204,21',
};
/** Brush radius, as a share of the picture's short side (living-motion.js: s[0]). */
export const BRUSHES = { s: 0.02, m: 0.045, l: 0.08 } as const;
export type Brush = keyof typeof BRUSHES;

export interface Stroke { r: number; pts: [number, number][] }
export interface PaintRegion { k: PaintKind; d?: [number, number]; root?: 'left' | 'right' | 'bottom' | 'top'; strokes: Stroke[] }

const MAX_REGIONS = 6;
const MAX_POINTS = 4000;
const round = (v: number) => Math.round(v * 1000) / 1000;

/** The `paint` field: regions as the server reads them ([r, x1, y1, ...] per stroke, negative r rubs out). */
export function encodePaint(regions: PaintRegion[]): string {
  const live = regions.filter((g) => g.strokes.some((s) => s.r > 0)).slice(0, MAX_REGIONS);
  if (!live.length) return '';
  let budget = MAX_POINTS;
  const out = live.map((g) => {
    const s = g.strokes.map((st) => {
      // Points closer than half the brush add nothing to the painted shape: dropped.
      const keep: [number, number][] = [];
      for (const p of st.pts) {
        const last = keep[keep.length - 1];
        if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > Math.abs(st.r) * 0.45) keep.push(p);
      }
      const pts = keep.slice(0, Math.max(1, budget));
      budget -= pts.length;
      return [round(st.r), ...pts.flatMap(([x, y]) => [round(x), round(y)])];
    }).filter((a) => a.length > 1);
    return { k: g.k, ...(g.d ? { d: g.d } : {}), ...(g.root ? { root: g.root } : {}), s };
  });
  return JSON.stringify(out);
}

/** The regions back from the field (undo/redo keep the field). */
export function decodePaint(raw: string | undefined): PaintRegion[] {
  if (!raw) return [];
  try {
    const list = JSON.parse(raw) as { k: PaintKind; d?: [number, number]; root?: 'left' | 'right' | 'bottom' | 'top'; s: number[][] }[];
    return list.filter((g) => PAINT_KINDS.includes(g.k)).map((g) => ({
      k: g.k, d: g.d, root: g.root,
      strokes: g.s.map((a) => {
        const pts: [number, number][] = [];
        for (let i = 1; i + 1 < a.length; i += 2) pts.push([a[i], a[i + 1]]);
        return { r: a[0], pts };
      }),
    }));
  } catch {
    return [];
  }
}

/**
 * Where the picture sits in a shape's frame, as canvas.js workingStill / extend.js put it (living-motion.js
 * placementOf, the same numbers): a scale k and a shift (ox, oy), in the frame's still pixels (W x H).
 */
export function placementOf(own: { w: number; h: number }, W: number, H: number, place: { mode: 'fill' | 'crop'; pos: number; zoom?: number; focus?: number } | null) {
  const pw = own.w; const ph = own.h;
  if (!place) {
    const k = Math.max(W / pw, H / ph);
    return { k, ox: (W - pw * k) / 2, oy: (H - ph * k) / 2 };
  }
  const pos = Math.max(0, Math.min(1, place.pos));
  if (place.mode === 'crop') {
    const k = Math.max(W / pw, H / ph);
    const sw = Math.round(pw * k); const sh = Math.round(ph * k);
    return { k: sw / pw, ox: -Math.max(0, Math.min(sw - W, Math.round((sw - W) * pos))), oy: -Math.max(0, Math.min(sh - H, Math.round((sh - H) * pos))) };
  }
  // Zoomed (a big subject kept big): a window of the long side round the subject is what is placed.
  let cx = 0; let cy = 0; let cw = pw; let ch = ph;
  const z = Number(place.zoom);
  if (z > 1.001) {
    const wide = pw / ph >= W / H;
    const len = wide ? pw : ph;
    const cover = wide ? (pw / ph) / (W / H) : (W / H) / (pw / ph);
    const keep = Math.max(16, Math.round(len / Math.min(z, cover)));
    const f = Math.max(0, Math.min(1, Number.isFinite(Number(place.focus)) ? Number(place.focus) : 0.5));
    const at = Math.max(0, Math.min(len - keep, Math.round(f * len - keep / 2)));
    if (wide) { cx = at; cw = keep; } else { cy = at; ch = keep; }
  }
  if (cw / ch >= W / H) {
    const h = Math.min(H, Math.round((ch * W) / cw));
    const k = W / cw;
    return { k, ox: cx ? -cx * k : 0, oy: Math.round((H - h) * pos) - (cy ? cy * k : 0) };
  }
  const w = Math.min(W, Math.round((cw * H) / ch));
  const k = H / ch;
  return { k, ox: Math.round((W - w) * pos) - (cx ? cx * k : 0), oy: cy ? -cy * k : 0 };
}

/** Frame (0..1 of the shape) <-> picture (0..1 of the original) for one placement. */
export function frameMap(own: { w: number; h: number }, frame: { w: number; h: number }, place: { mode: 'fill' | 'crop'; pos: number; zoom?: number; focus?: number } | null) {
  // The still the cover is drawn from is the frame at 1.25x (canvas.js frameOf); shares are the same either way.
  const W = Math.max(2, Math.round((frame.w * 1.25) / 2) * 2);
  const H = Math.max(2, Math.round((frame.h * 1.25) / 2) * 2);
  const at = placementOf(own, W, H, place);
  return {
    toPicture: (u: number, v: number): [number, number] => [(u * W - at.ox) / (at.k * own.w), (v * H - at.oy) / (at.k * own.h)],
    toFrame: (x: number, y: number): [number, number] => [(at.ox + x * own.w * at.k) / W, (at.oy + y * own.h * at.k) / H],
    /** A brush radius (share of the picture's short side) in shares of the frame's width. */
    radiusInFrame: (r: number) => (Math.abs(r) * Math.min(own.w, own.h) * at.k) / W,
    radiusFromFrame: (rFrameW: number) => (rFrameW * W) / (Math.min(own.w, own.h) * at.k),
  };
}
